/**
 * RequestDeliverablesStep — the "Deliverables" section.
 *
 * Outside the registry by construction: it is **not** in `REQUEST_FORM_STEP_REGISTRY` /
 * `STEP_COMPONENTS` / `stepSectionMap`, because `buildRequestFormSteps` and
 * `buildDetailFormSteps` are the same function over the same registry, so anything added
 * there gets wizard footer navigation, scope handling and step-order semantics it does not
 * want. It follows the `impersonation-audit` precedent instead: hardcoded sidebar item plus
 * a render branch — now in **both** `RequestDetail` and `CreateRequest`, since cycle 4 makes
 * the section reachable from the moment a request is created (FR11).
 *
 * It is **controlled**, and holds nothing. Cycle 4b made deliverables behave like every
 * other request field, so the list and the pending changes belong to whatever owns the save:
 * `useDeliverablesEditSession` on Request Detail, `formData` on Create Request. This
 * component only renders and reports.
 */

import React from 'react';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';

import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { fontSize, lineHeight } from '../../theme/typography';
import { RequestDeliverablesEditor } from '../deliverables/RequestDeliverablesEditor';

import type {
  RequestDeliverable,
  RequestDeliverableEntry,
  RequestDeliverableType,
} from '../../types/requestDeliverables';

export interface RequestDeliverablesStepProps {
  deliverables: RequestDeliverable[];
  onAdd: (entries: RequestDeliverableEntry[]) => void;
  onEdit: (itemType: RequestDeliverableType, comment: string) => void;
  onRemove: (itemType: RequestDeliverableType) => void;
  /**
   * Renders the deliverables and none of the controls for changing them. Three reasons
   * reach this: a Closed request (FR18), a saved request that is not in edit mode — the
   * same "press Edit first" rule every other field on the screen follows — and a viewer
   * whose role cannot write this field at this stage.
   */
  readOnly?: boolean;
  /**
   * Which of those three it is. Only drives the copy, and the copy is the point: telling
   * someone to "press Edit" when pressing Edit will not help them is the half of this
   * section's problem that removing the controls does not fix.
   */
  readOnlyReason?: 'closed' | 'view' | 'no-permission' | 'untrusted-base';
  isLoading?: boolean;
  error?: string | null;
  /**
   * Non-error explanation of what is on screen — offline with no local copy. Styled as
   * secondary copy, not as an error, because nothing failed.
   */
  notice?: string | null;
}

const SUBTITLE_EDITABLE =
  'What this request is asking Vertiv to produce. Changes are saved with the rest of the request.';
const SUBTITLE_VIEW =
  'What this request is asking Vertiv to produce. Press Edit to add, change or remove items.';
const SUBTITLE_CLOSED =
  'What this request asked Vertiv to produce. This request is closed, so these can no longer be changed.';
const SUBTITLE_NO_PERMISSION =
  'What this request is asking Vertiv to produce. You do not have permission to change these.';

/**
 * Shown when the device could not establish what is actually stored — offline having never
 * cached this request, or a failed load. Editing is blocked rather than merely discouraged:
 * a save sends the whole list, so changing anything from a list we cannot vouch for would
 * delete the rows the device never saw. (MR !489 review, finding 01.)
 */
const SUBTITLE_UNTRUSTED_BASE =
  'What this request is asking Vertiv to produce. This list could not be loaded, so it cannot be changed here yet — reconnect or reload to edit.';

export const RequestDeliverablesStep: React.FC<
  RequestDeliverablesStepProps
> = ({
  deliverables,
  onAdd,
  onEdit,
  onRemove,
  readOnly = false,
  readOnlyReason = 'view',
  isLoading = false,
  error = null,
  notice = null,
}) => {
  const subtitle = !readOnly
    ? SUBTITLE_EDITABLE
    : readOnlyReason === 'closed'
    ? SUBTITLE_CLOSED
    : readOnlyReason === 'no-permission'
    ? SUBTITLE_NO_PERMISSION
    : readOnlyReason === 'untrusted-base'
    ? SUBTITLE_UNTRUSTED_BASE
    : SUBTITLE_VIEW;

  /**
   * Whether to name the types that were not requested, so a reader can see what else could be
   * asked for. Derived here rather than in the editor, which stays ignorant of WHY it is
   * read-only, and derived beside the subtitle so the two readings of `readOnlyReason` cannot
   * drift apart.
   *
   * Everything but `closed` and `untrusted-base`: on a Closed request nothing can be requested
   * any more, so a list of what was not asked for reads as a reproach rather than an option;
   * and on an untrusted base we do not know what WAS requested, so naming the remainder would
   * be a guess presented as fact. (MR !489 review, finding 01.)
   */
  const showAvailableOptions =
    readOnly &&
    readOnlyReason !== 'closed' &&
    readOnlyReason !== 'untrusted-base';

  return (
    <View style={styles.container} testID="request-deliverables-step">
      <Text style={styles.title}>Deliverables</Text>
      <Text style={styles.subtitle}>{subtitle}</Text>

      {!!error && (
        <Text style={styles.errorText} testID="request-deliverables-step-error">
          {error}
        </Text>
      )}

      {!error && !!notice && (
        <Text
          style={styles.noticeText}
          testID="request-deliverables-step-notice"
        >
          {notice}
        </Text>
      )}

      {isLoading ? (
        <View style={styles.stateContainer}>
          <ActivityIndicator size="small" color={colors.textSecondary} />
          <Text style={styles.loadingText}>Loading deliverables...</Text>
        </View>
      ) : (
        <RequestDeliverablesEditor
          deliverables={deliverables}
          onAdd={onAdd}
          onEdit={onEdit}
          onRemove={onRemove}
          disabled={readOnly}
          showAvailableOptions={showAvailableOptions}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: spacing.cardPadding,
  },
  title: {
    fontSize: fontSize.sectionTitle,
    lineHeight: lineHeight.sectionTitle,
    fontWeight: '600',
    color: colors.textPrimary,
    marginBottom: spacing.subsectionTitleTight,
  },
  subtitle: {
    fontSize: fontSize.cardSubtitle,
    lineHeight: lineHeight.formControl,
    color: colors.textSecondary,
  },
  stateContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.formControlIconGap,
    paddingVertical: spacing.pdPagePaddingVertical,
  },
  loadingText: {
    fontSize: fontSize.formLabel,
    color: colors.textSecondary,
  },
  errorText: {
    fontSize: fontSize.cardSubtitle,
    lineHeight: lineHeight.formControl,
    color: colors.error,
    marginTop: spacing.formInlineFeedbackGap,
  },
  noticeText: {
    fontSize: fontSize.cardSubtitle,
    lineHeight: lineHeight.formControl,
    color: colors.textSecondary,
    marginTop: spacing.formInlineFeedbackGap,
  },
});
